import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {ProgressService} from "../../../shared/services/progress.service";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {ActivatedRoute, NavigationExtras} from "@angular/router";
import {SettingsDataService} from "../../../shared/services/settings-data.service";
import {Setting} from "../../../shared/services/models/settings-service.model";
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {finalize, forkJoin, of} from "rxjs";
import {FormArray, FormBuilder, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {catchError, map} from "rxjs/operators";
import {CardModule} from "primeng/card";
import {InputTextModule} from "primeng/inputtext";
import {ToggleSwitch} from "primeng/toggleswitch";
import {ButtonModule} from "primeng/button";
import {SelectModule} from "primeng/select";
import {NgOptimizedImage, UpperCasePipe} from "@angular/common";
import {DetailHeaderComponent} from "../../../shared/components/config-detail/detail-header/detail-header.component";
import {SettingFormGroup} from "../../../settings/services/models/setting-form-group.model";
import {ConfigurationService} from "../../../shared/services/configuration.service";
import {ConfigDetailComponent} from "../../../shared/components/config-detail/config-detail.component";
import {ConfigRouteEnum} from "../../../shared/enums/config-route.enum";
import {DetailBodyComponent} from "../../../shared/components/config-detail/detail-body/detail-body.component";
import {
    DetailHeaderActionComponent
} from "../../../shared/components/config-detail/detail-header-action/detail-header-action.component";
import {RefreshService} from "../../../shared/services/refresh.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'app-translation-edit',
    imports: [
        TranslateModule,
        FormsModule,
        CardModule,
        InputTextModule,
        ToggleSwitch,
        ButtonModule,
        SelectModule,
        ReactiveFormsModule,
        DetailHeaderComponent,
        NgOptimizedImage,
        UpperCasePipe,
        ConfigDetailComponent,
        DetailBodyComponent,
        DetailHeaderActionComponent,
    ],
    templateUrl: './translation-edit.component.html',
    styleUrls: ['./translation-edit.component.scss'],
    standalone: true
})
export class TranslationEditComponent implements OnInit {
    @Input() navigateBack?: { commands: any[], extras?: NavigationExtras };

    private readonly progressService = inject(ProgressService);
    private readonly snackBar = inject(SnackbarService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly settingsDataService = inject(SettingsDataService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly translate = inject(TranslateService);
    private readonly config = inject(ConfigurationService).configuration;
    private readonly destroyRef = inject(DestroyRef);
    private readonly refreshService = inject(RefreshService);


    protected readonly ConfigRouteEnum = ConfigRouteEnum;

    applicationName: string | undefined;
    identification: string | undefined;

    settings: Setting[] = [];
    logoUrl: string | null = this.config.app.defaultLogo;

    USABLE_LANGUAGES = ['lngDE', 'lngEN', 'lngES'] as const; //TODO: PRODEV-4602

    get languageOptions() {
        return this.USABLE_LANGUAGES.map(language => ({
            label: this.translate.instant('translation.' + language),
            value: language,
        }));
    }

    form = this.formBuilder.group({
        translations: this.formBuilder.array<SettingFormGroup>([])
    });

    get translations(): FormArray<SettingFormGroup> {
        return this.form.get('translations') as FormArray<SettingFormGroup>;
    }

    private buildTranslationGroup(s: Setting): SettingFormGroup {
        return this.formBuilder.group({
            id: [s.id ?? null],
            tenantName: [s.tenantName, Validators.required],
            applicationSettings: [s.applicationSettings ?? ''],
            active: [s.active ?? true],
            applicationName: [s.applicationName, Validators.required],
            identification: [s.identification, Validators.required],
            participantId: [s.participantId ?? ''],
            participantPartnerId: [s.participantPartnerId ?? ''],
            relationType: [s.relationType ?? ''],
            applicationType: [s.applicationType ?? null],
        }) as SettingFormGroup;
    }

    ngOnInit(): void {
        this.subscribeToRoute()
        this.loadTranslations()

        this.refreshService.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.loadTranslations();
        });

    }

    private subscribeToRoute(): void {
        this.activatedRoute.paramMap.subscribe(paramMap => {
            const applicationName = paramMap.get('searchName');
            const identification = paramMap.get('identification');

            if (!applicationName || !identification) return;

            this.applicationName = applicationName;
            this.identification = identification;
        });
    }

    private loadTranslations(): void {
        this.progressService.startLoading();
        this.settingsDataService
            .findAllByIdentificationAndApplicationName({
                identification: this.identification!,
                name: this.applicationName!
            })
            .pipe(finalize(() => this.progressService.stopLoading()))
            .subscribe(settings => {
                this.settings = [];
                this.translations.clear();

                if (settings.length === 0) {
                    const empty = this.createEmptySetting(
                        this.applicationName!,
                        this.identification!,
                        this.getNextLanguage()
                    );

                    this.settings.push(empty);
                    this.translations.push(this.buildTranslationGroup(empty));
                } else {
                    this.settings = settings;
                    this.translations.clear();
                    settings.forEach(s => this.translations.push(this.buildTranslationGroup(s)));
                }
            })
    }

    addTranslation(): void {
        const empty = this.createEmptySetting(this.applicationName!, this.identification!, this.getNextLanguage());
        this.settings.push(empty);
        this.translations.push(this.buildTranslationGroup(empty));
    }


    saveAllTranslations(): void {
        if (this.translations.invalid) {
            this.translations.markAllAsTouched();
            return;
        }

        const observables = this.translations.controls.map(formGroup => {
            const setting: Setting = formGroup.getRawValue() as Setting;
            if (setting.id === null) {
                delete setting.id;
            }

            const request = setting.id
                ? this.settingsDataService.updateSetting(setting)
                : this.settingsDataService.addSetting(setting);

            return request.pipe(
                catchError(() => of(null))
            );
        });

        if (observables.length === 0) return;

        this.progressService.startLoading();
        forkJoin(observables).pipe(
            finalize(() => this.progressService.stopLoading())
        ).subscribe(results => {
            const success = results.every(r => r !== null);
            if (success) {
                this.snackBar.Success(this.translate.instant("snackbar.translation.saved"));
            } else {
                this.snackBar.Error(this.translate.instant("snackbar.translation.save_error"));
            }
            this.loadTranslations();
        });
    }

    private createEmptySetting(applicationName: string, identification: string, tenantName: string): Setting {
        return {
            applicationName,
            identification,
            tenantName,
            participantId: '',
            participantPartnerId: '',
            relationType: '',
            applicationType: null,
            active: true,
            applicationSettings: '',
            id: null,
        };
    }

    deleteTranslation(index: number): void {
        const setting = this.settings[index];

        if (!setting.id) {
            this.settings.splice(index, 1);
            this.translations.removeAt(index);
            return;
        }

        this.progressService.startLoading();

        this.settingsDataService.deleteSetting(setting).pipe(
            finalize(() => this.progressService.stopLoading()),
            map(() => true),
            catchError(() => {
                this.snackBar.Error(this.translate.instant("snackbar.translation.delete_error"));
                return of(false);
            })
        ).subscribe(success => {
            if (!success) return;

            this.loadTranslations();

            this.snackBar.Success(this.translate.instant("snackbar.translation.deleted", { name: setting.applicationSettings }));
        });
    }


    getNextLanguage(): string {
        const used = new Set(this.translations.controls.map(c => c.value.tenantName));
        return this.USABLE_LANGUAGES.find(lng => !used.has(lng)) ?? '';
    }
}
